import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import CoachMultiSelect from "./CoachMultiSelect";
import HintTooltip from "./HintTooltip";
import { fetchMembershipPlans } from "./membership-plans-fetch";
import {
  BOOKING_CUTOFF_OPTIONS,
  normalizeBookingCutoff,
} from "./scheduling-limit-period";

/** Same as Create Class / Scheduling Settings — POST body membership & user-type ids */
const USER_TYPES = [
  { id: "push", label: "PUSH (Programs only)" },
  { id: "pull", label: "PULL (1:1 Coaching)" },
];

/** Duration options from 15 to 120 minutes in 15-minute steps */
const DURATION_OPTIONS = [15, 30, 45, 60];

/** Monday = 0 … Sunday = 6 */
const WEEK_DAYS = [
  { key: "mon", day_of_week: 0, label: "Monday" },
  { key: "tue", day_of_week: 1, label: "Tuesday" },
  { key: "wed", day_of_week: 2, label: "Wednesday" },
  { key: "thu", day_of_week: 3, label: "Thursday" },
  { key: "fri", day_of_week: 4, label: "Friday" },
  { key: "sat", day_of_week: 5, label: "Saturday" },
  { key: "sun", day_of_week: 6, label: "Sunday" },
];

function newSlot() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    start: "11:30",
    end: "23:30",
  };
}

function emptyDayRanges() {
  return WEEK_DAYS.reduce((acc, d) => {
    acc[d.key] = [newSlot()];
    return acc;
  }, {});
}

/** Normalize HTML time input to API `HH:mm` (24h), e.g. "9:00" → "09:00".
 * Some browsers send seconds from time inputs (`06:00:00`) — accept and strip. */
function formatTimeHHmm(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "";
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

function cloneSlotsForPaste(slots) {
  const t = Date.now();
  return (slots || []).map((s, i) => ({
    id: `${t}-${i}-${Math.random().toString(36).slice(2, 9)}`,
    start: s.start,
    end: s.end,
  }));
}

function hhmmForTimeInput(s) {
  if (!s || typeof s !== "string") return "";
  const m = s.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/** Stable key for id + windows so we do not re-apply when parent passes a new object ref with the same data. */
function snapshotKeyForPtRecord(r) {
  if (!r || typeof r !== "object") return "";
  const id = String(r._id ?? r.id ?? "");
  const wins = r.windows;
  if (!Array.isArray(wins)) return `${id}:`;
  const wk = wins
    .map((w) => {
      const dow = w.day_of_week ?? w.dayOfWeek ?? "";
      const st = String(w.start_time ?? w.startTime ?? "");
      const et = String(w.end_time ?? w.endTime ?? "");
      return `${dow}:${st}:${et}`;
    })
    .sort()
    .join("|");
  return `${id}:${wk}`;
}

/** Map API `windows[]` to PT modal `dayRanges` state. */
function windowsToDayRanges(windows) {
  const base = WEEK_DAYS.reduce((acc, d) => {
    acc[d.key] = [];
    return acc;
  }, {});
  if (!Array.isArray(windows) || windows.length === 0) return base;
  const byDay = {};
  windows.forEach((w, wi) => {
    const dow = w.day_of_week ?? w.dayOfWeek;
    if (dow == null || dow < 0 || dow > 6) return;
    const day = WEEK_DAYS.find((d) => d.day_of_week === dow);
    if (!day) return;
    const key = day.key;
    if (!byDay[key]) byDay[key] = [];
    const st = hhmmForTimeInput(w.start_time ?? w.startTime ?? "");
    const et = hhmmForTimeInput(w.end_time ?? w.endTime ?? "");
    if (!st && !et) return;
    byDay[key].push({
      id: `pt-${key}-${wi}-${st}-${et}`,
      start: st,
      end: et,
    });
  });
  const next = { ...base };
  for (const k of Object.keys(next)) {
    if (byDay[k]?.length) next[k] = byDay[k];
  }
  return next;
}

const CreatePtSessionModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialRecord,
  initialScrollSection = null,
  showAccessPermissions = true,
  hasPressScheduling = false,
  hasPullScheduling = false,
  canPushUser = true,
  canPullUser = true,
}) => {
  const { locals } = useRillContext();
  const activeUser = locals?.activeUser;
  const coachId = activeUser?._id != null ? String(activeUser._id) : "";

  const [name, setName] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [capacity, setCapacity] = useState("1");
  const [bookingCutoff, setBookingCutoff] = useState("");
  const [location, setLocation] = useState("");
  const [dayRanges, setDayRanges] = useState(emptyDayRanges);
  /** @type {Array<{ start: string, end: string }> | null} */
  const [copiedSlots, setCopiedSlots] = useState(null);

  const [pressEnabled, setPressEnabled] = useState(false);
  const [pullEnabled, setPullEnabled] = useState(false);
  const [accessMode, setAccessMode] = useState("user_type");
  const [userTypes, setUserTypes] = useState({ push: true, pull: false });
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState({});

  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PRESS restriction matches gym memberships/plans, so only PRESS-scheduling
  // coaches get it; hidden, the stored restriction is left untouched.
  const showPressToggle =
    showAccessPermissions &&
    (hasPressScheduling || Boolean(initialRecord?.is_restricted));
  const showPullToggle = showAccessPermissions && hasPullScheduling;
  const pullActive = showPullToggle && pullEnabled;

  const visibleUserTypes = USER_TYPES.filter(
    ({ id }) => (id === "push" && canPushUser) || (id === "pull" && canPullUser)
  );

  const userTypeAvailable = visibleUserTypes.length > 0;
  const accessModeEffective = userTypeAvailable ? accessMode : "membership";

  const selectAccessMode = (mode) => {
    setAccessMode(mode);
    if (mode === "user_type") {
      setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
    } else {
      setUserTypes({ push: false, pull: false });
    }
  };

  const restrictedNoSelection =
    pressEnabled &&
    (accessModeEffective === "user_type"
      ? !visibleUserTypes.some(({ id }) => userTypes[id])
      : !Object.keys(memberships).some((id) => memberships[id]));
  const [selectedCoachIds, setSelectedCoachIds] = useState([]);
  const accessSectionRef = useRef(null);

  const resetForm = useCallback(() => {
    setName("");
    setDurationMinutes(30);
    setCapacity("1");
    setBookingCutoff("");
    setLocation("");
    setDayRanges(emptyDayRanges());
    setCopiedSlots(null);
    setPressEnabled(false);
    setPullEnabled(false);
    setAccessMode("user_type");
    setUserTypes({ push: true, pull: false });
    setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
    setSelectedCoachIds([]);
    setSubmitError(null);
  }, [plans]);

  const applyPtRecord = useCallback(
    (r) => {
      if (!r || typeof r !== "object") return;
      setName(r.name ?? r.title ?? "");
      setDurationMinutes(Number(r.duration_minutes) || 30);
      setCapacity(String(r.capacity ?? "1"));
      setBookingCutoff(
        r.booking_cutoff == null ? "" : String(r.booking_cutoff)
      );
      setLocation((r.location ?? "").trim());
      const aut = Array.isArray(r.allowed_user_types)
        ? r.allowed_user_types
        : [];
      const amp = Array.isArray(r.allowed_membership_plans)
        ? r.allowed_membership_plans
        : [];

      const byPlans = amp.length > 0;

      setPressEnabled(Boolean(r.is_restricted));
      setPullEnabled(Boolean(r.pull_only));
      setUserTypes({
        push: !byPlans && aut.includes("push"),
        pull: !byPlans && aut.includes("pull"),
      });
      setMemberships(
        Object.fromEntries(plans.map(({ id }) => [id, amp.includes(id)]))
      );
      setAccessMode(byPlans ? "membership" : "user_type");
      if (Array.isArray(r.coach_ids) && r.coach_ids.length > 0) {
        setSelectedCoachIds(r.coach_ids.map(String));
      } else if (r.coach_id != null && String(r.coach_id)) {
        setSelectedCoachIds([String(r.coach_id)]);
      } else {
        setSelectedCoachIds([]);
      }
      const win = r.windows;
      if (Array.isArray(win) && win.length > 0) {
        /* Copy so we never share the API array reference (avoids duplicate slots if it mutates). */
        const copy = win.map((w) => ({ ...w }));
        setDayRanges(windowsToDayRanges(copy));
      } else {
        setDayRanges(emptyDayRanges());
      }
      setCopiedSlots(null);
      setSubmitError(null);
    },
    [plans]
  );

  useEffect(() => {
    let cancelled = false;
    fetchMembershipPlans().then((data) => {
      if (!cancelled) setPlans(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isOpen || initialRecord || plans.length === 0) return;
    setMemberships((prev) =>
      Object.keys(prev).length === 0
        ? Object.fromEntries(plans.map(({ id }) => [id, false]))
        : prev
    );
  }, [isOpen, initialRecord, plans]);

  const lastAppliedSnapshotRef = useRef(null);
  const lastAppliedPlansRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      lastAppliedSnapshotRef.current = null;
      lastAppliedPlansRef.current = null;
      return;
    }
    if (!initialRecord) {
      if (lastAppliedSnapshotRef.current !== "create") {
        resetForm();
        lastAppliedSnapshotRef.current = "create";
        lastAppliedPlansRef.current = null;
      }
      return;
    }
    const snap = snapshotKeyForPtRecord(initialRecord);
    if (
      lastAppliedSnapshotRef.current === snap &&
      lastAppliedPlansRef.current === plans
    ) {
      return;
    }
    lastAppliedSnapshotRef.current = snap;
    lastAppliedPlansRef.current = plans;
    applyPtRecord(initialRecord);
  }, [isOpen, initialRecord, applyPtRecord, resetForm, plans]);

  useLayoutEffect(() => {
    if (!isOpen || initialScrollSection !== "access_permissions") return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        accessSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, initialScrollSection]);

  const handleClose = (e) => {
    e?.preventDefault?.();
    onClose();
  };

  const updateSlot = (dayKey, index, field, value) => {
    setDayRanges((prev) => {
      const next = { ...prev };
      const list = [...(next[dayKey] || [])];
      list[index] = { ...list[index], [field]: value };
      next[dayKey] = list;
      return next;
    });
  };

  const addSlot = (dayKey) => {
    setDayRanges((prev) => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), newSlot()],
    }));
  };

  const removeSlot = (dayKey, index) => {
    setDayRanges((prev) => {
      const list = [...(prev[dayKey] || [])];
      if (list.length <= 1) {
        return { ...prev, [dayKey]: [] };
      }
      list.splice(index, 1);
      return { ...prev, [dayKey]: list };
    });
  };

  const copyDaySchedule = (dayKey) => {
    const slots = dayRanges[dayKey] || [];
    setCopiedSlots(slots.map(({ start, end }) => ({ start, end })));
  };

  const pasteDaySchedule = (dayKey) => {
    if (!copiedSlots?.length) return;
    setDayRanges((prev) => ({
      ...prev,
      [dayKey]: cloneSlotsForPaste(copiedSlots),
    }));
  };

  const toggleUserType = (id) => {
    setUserTypes((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleMembership = (id) => {
    setMemberships((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const allMembershipsChecked =
    plans.length > 0 && plans.every(({ id }) => memberships[id]);
  const setAllMemberships = (checked) => {
    const next = {};
    for (const { id } of plans) next[id] = checked;
    setMemberships(next);
  };

  /**
   * Weekly availability as a plain JSON array (not form-style `windows[0][…]` keys).
   * Multiple rows per day are separate objects, e.g. two blocks on Saturday:
   * `[{ day_of_week: 5, … }, { day_of_week: 5, … }]`.
   */
  const buildWindows = () => {
    const windows = [];
    for (const { key, day_of_week } of WEEK_DAYS) {
      const ranges = dayRanges[key] || [];
      for (const r of ranges) {
        if (!r.start || !r.end) continue;
        const start_time = formatTimeHHmm(r.start);
        const end_time = formatTimeHHmm(r.end);
        if (start_time && end_time) {
          windows.push({
            day_of_week: Number(day_of_week),
            start_time,
            end_time,
          });
        }
      }
    }
    return windows;
  };

  /** Create/update body for POST/PUT — `Content-Type: application/json`, `credentials: 'same-origin'`. */
  const getCreatePtSessionPayload = () => {
    const rawWindows = buildWindows();
    const windows = rawWindows.map(({ day_of_week, start_time, end_time }) => ({
      day_of_week: Number(day_of_week),
      start_time: String(start_time),
      end_time: String(end_time),
    }));

    // PRESS restriction (is_restricted + user types + plans) and the PULL-only
    // audience (pull_only) are independent — a session may carry both.
    const allowed_user_types =
      pressEnabled && accessModeEffective === "user_type"
        ? visibleUserTypes.filter(({ id }) => userTypes[id]).map(({ id }) => id)
        : [];
    const allowed_membership_plans =
      pressEnabled && accessModeEffective === "membership"
        ? Object.keys(memberships).filter((id) => memberships[id])
        : [];

    // Core shape (matches API examples): name → schedule_type → windows[], then coach + access + limits.
    return {
      name: name.trim(),
      duration_minutes: Number(durationMinutes) || 0,
      capacity: Number(capacity) || 0,
      booking_cutoff: normalizeBookingCutoff(bookingCutoff),
      location: location.trim(),
      schedule_type: "custom",
      windows,
      coach_id: coachId,
      ...(selectedCoachIds.length > 0 ? { coach_ids: selectedCoachIds } : {}),
      is_restricted: pressEnabled,
      allowed_user_types: pressEnabled ? allowed_user_types : [],
      allowed_membership_plans: pressEnabled ? allowed_membership_plans : [],
      pull_only: pullActive,
    };
  };

  const canSubmit =
    name.trim().length > 0 &&
    coachId &&
    Number(durationMinutes) > 0 &&
    buildWindows().length > 0;

  const handleCreatePtSession = async (e) => {
    e?.preventDefault?.();
    if (restrictedNoSelection) {
      setSubmitError(
        accessModeEffective === "user_type"
          ? "Select at least one user type when Press Users is enabled."
          : "Select at least one membership plan when Press Users is enabled."
      );
      return;
    }
    if (typeof onConfirm !== "function") {
      handleClose(e);
      return;
    }
    if (!canSubmit) {
      setSubmitError(
        !coachId
          ? "You must be signed in as a coach to create a PT session."
          : "Add a name and at least one complete time window (start and end)."
      );
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload = getCreatePtSessionPayload();
      await Promise.resolve(onConfirm(payload));
      handleClose(e);
    } catch (err) {
      setSubmitError(
        err?.message ||
          (initialRecord?._id || initialRecord?.id
            ? "Failed to save PT session"
            : "Failed to create PT session")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const editId = initialRecord?._id ?? initialRecord?.id;
  const isEdit = Boolean(editId);

  if (!isOpen) return null;

  return (
    <div
      className="Modal-Box create-class-modal create-pt-session-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-pt-session-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={handleClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-pt-session-modal-title">
            {isEdit ? "Edit PT Session" : "Create PT Session"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>
        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Basic Details
              </h3>
              <p className="create-class-modal-section-desc">
                Define the core settings for this PT booking type.
              </p>
            </div>
            <div className="create-class-modal-form create-pt-modal-form">
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label htmlFor="create-pt-event-name">Event Name</label>
                  <input
                    id="create-pt-event-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Meeting with John"
                    aria-label="Event name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="create-pt-duration">Duration</label>
                  <div className="create-class-modal-select-wrapper">
                    <select
                      id="create-pt-duration"
                      value={durationMinutes}
                      onChange={(e) =>
                        setDurationMinutes(Number(e.target.value))
                      }
                      aria-label="Duration in minutes"
                    >
                      {DURATION_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {`${n} Mins`}
                        </option>
                      ))}
                    </select>
                    <img
                      src="/img/new-icons/chevron-down.svg"
                      alt=""
                      className="create-class-modal-select-chevron"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label htmlFor="create-pt-coach">Coaches</label>
                  <CoachMultiSelect
                    id="create-pt-coach"
                    value={selectedCoachIds}
                    onChange={setSelectedCoachIds}
                  />
                </div>
                <div className="form-group">
                  <label
                    htmlFor="create-pt-capacity"
                    className="create-class-modal-label-with-hint"
                  >
                    Capacity
                    <HintTooltip
                      label="Capacity help"
                      message="Number of members allowed per session"
                    />
                  </label>
                  <input
                    id="create-pt-capacity"
                    type="number"
                    min={1}
                    step={1}
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    placeholder="e.g. 1"
                    aria-label="Capacity"
                  />
                </div>
              </div>
              <div className="create-class-modal-form-two-cols">
                <div className="form-group">
                  <label htmlFor="create-pt-location">Location</label>
                  <input
                    id="create-pt-location"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onBlur={() => setLocation((v) => v.trim())}
                    placeholder="e.g. Evolve gym"
                    title={location || undefined}
                    aria-label="Location"
                  />
                </div>
                <div className="form-group">
                  <label
                    htmlFor="create-pt-booking-cutoff"
                    className="create-class-modal-label-with-hint"
                  >
                    Booking cutoff
                    <HintTooltip
                      label="Booking cutoff help"
                      message="Blocks new bookings this amount of time before the event starts."
                    />
                  </label>
                  <div className="create-class-modal-select-wrapper">
                    <select
                      id="create-pt-booking-cutoff"
                      value={bookingCutoff}
                      onChange={(e) => setBookingCutoff(e.target.value)}
                      aria-label="Booking cutoff in hours"
                    >
                      {BOOKING_CUTOFF_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <img
                      src="/img/new-icons/chevron-down.svg"
                      alt=""
                      className="create-class-modal-select-chevron"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols create-pt-session-availability-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Availability</h3>
              <p className="create-class-modal-section-desc">
                <strong>(Weekly Recurring)</strong>
                <br />
                Set the days and time windows when this session can be booked.
              </p>
            </div>
            <div className="create-class-modal-form create-pt-session-availability-form">
              <ul className="create-pt-session-week-list">
                {WEEK_DAYS.flatMap(({ key, label }) => {
                  const slots = dayRanges[key] || [];
                  if (slots.length === 0) {
                    return [
                      <li
                        key={`${key}-unavailable`}
                        className="create-pt-session-day-row"
                      >
                        <span className="create-pt-session-day-cell">
                          {label}
                        </span>
                        <span className="create-pt-session-unavailable-label">
                          Unavailable
                        </span>
                        <div className="create-pt-session-day-actions">
                          <button
                            type="button"
                            className="create-pt-session-icon-btn"
                            onClick={() => addSlot(key)}
                            aria-label={`Add time range on ${label}`}
                            title="Add time range"
                          >
                            <img
                              src="/img/new-icons/add-circle-outline.svg"
                              alt=""
                              aria-hidden="true"
                              className="create-pt-session-add-icon"
                            />
                          </button>
                        </div>
                      </li>,
                    ];
                  }
                  return slots.map((slot, index) => (
                    <li
                      key={`${key}-${slot.id}`}
                      className={
                        "create-pt-session-day-row" +
                        (index > 0
                          ? " create-pt-session-day-row--continued"
                          : "")
                      }
                    >
                      {index === 0 ? (
                        <span className="create-pt-session-day-cell">
                          {label}
                        </span>
                      ) : (
                        <span
                          className="create-pt-session-day-cell create-pt-session-day-cell--empty"
                          aria-hidden="true"
                        />
                      )}
                      <div className="create-pt-session-range-pill">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) =>
                            updateSlot(key, index, "start", e.target.value)
                          }
                          aria-label={`${label} start time, range ${index + 1}`}
                          className="create-pt-session-time-input"
                        />
                        <span
                          className="create-pt-session-range-sep"
                          aria-hidden="true"
                        >
                          -
                        </span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) =>
                            updateSlot(key, index, "end", e.target.value)
                          }
                          aria-label={`${label} end time, range ${index + 1}`}
                          className="create-pt-session-time-input"
                        />
                      </div>
                      <div className="create-pt-session-day-actions">
                        <button
                          type="button"
                          className="create-pt-session-icon-btn"
                          onClick={() => removeSlot(key, index)}
                          aria-label={`Remove time range ${index + 1} on ${label}`}
                          title="Remove this time range"
                        >
                          <img
                            src="/img/new-icons/cross-light.svg"
                            alt=""
                            aria-hidden="true"
                            className="create-pt-session-icon-cross"
                          />
                        </button>
                        {index === 0 && (
                          <>
                            <button
                              type="button"
                              className="create-pt-session-icon-btn"
                              onClick={() => addSlot(key)}
                              aria-label={`Add time range on ${label}`}
                              title="Add time range"
                            >
                              <img
                                src="/img/new-icons/add-circle-outline.svg"
                                alt=""
                                aria-hidden="true"
                                className="create-pt-session-add-icon"
                              />
                            </button>
                            <button
                              type="button"
                              className="create-pt-session-icon-btn"
                              onClick={() => copyDaySchedule(key)}
                              aria-label={`Copy ${label} schedule`}
                              title="Copy this day’s time windows"
                            >
                              <img
                                src="/img/new-icons/duplicate.svg"
                                alt=""
                                aria-hidden="true"
                                className="create-pt-session-duplicate-icon"
                              />
                            </button>
                            {copiedSlots && (
                              <button
                                type="button"
                                className="create-pt-session-icon-btn"
                                onClick={() => pasteDaySchedule(key)}
                                aria-label={`Paste schedule into ${label}`}
                                title="Paste copied schedule"
                              >
                                <img
                                  src="/img/new-icons/clipboard-notes.svg"
                                  alt=""
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ));
                })}
              </ul>
            </div>
          </div>

          {(showPressToggle || showPullToggle) && (
            <>
              <hr className="create-class-modal-separator" />

              <div
                ref={accessSectionRef}
                id="scheduling-edit-modal-access-permissions-pt"
                className="create-class-modal-cols create-class-modal-access-booking"
              >
                <div className="create-class-modal-section-head">
                  <h3 className="create-class-modal-section-title">
                    Access &amp; Permissions
                  </h3>
                  <p className="create-class-modal-section-desc">
                    Control who can view and book this session.
                  </p>
                  <div className="scheduling-settings-info create-pt-session-access-hint">
                    <img
                      src="/img/new-icons/vuesax-linear-info-circle.svg"
                      alt=""
                      className="scheduling-settings-info-icon"
                    />
                    <span>
                      Access &amp; Permissions applies to this event only
                    </span>
                  </div>
                </div>
                <div className="create-class-modal-form scheduling-access-options-wrapper">
                  <div className="scheduling-access-options">
                    {showPressToggle && (
                      <div className="scheduling-access-row scheduling-access-row-switch scheduling-access-row-press">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label="Press users access"
                          aria-checked={pressEnabled}
                          checked={pressEnabled}
                          onChange={() => setPressEnabled(!pressEnabled)}
                        />
                        <span className="scheduling-access-row-text">
                          <span className="scheduling-access-row-title">
                            PRESS USERS{" "}
                            <span className="scheduling-access-row-title-hint">
                              (Gym Members)
                            </span>
                          </span>
                          <span className="scheduling-access-row-desc">
                            Only selected user types or membership plans can
                            view and book this session.
                          </span>
                        </span>
                      </div>
                    )}
                    {showPressToggle && pressEnabled && (
                      <>
                        <div
                          className="scheduling-access-mode"
                          role="radiogroup"
                          aria-label="Restrict access by"
                        >
                          {userTypeAvailable && (
                            <label className="radio-input">
                              <input
                                type="radio"
                                name="create-pt-access-mode"
                                checked={accessModeEffective === "user_type"}
                                onChange={() => selectAccessMode("user_type")}
                              />
                              <span className="radio" aria-hidden="true" />
                              <span className="text">By user type</span>
                            </label>
                          )}
                          <label className="radio-input">
                            <input
                              type="radio"
                              name="create-pt-access-mode"
                              checked={accessModeEffective === "membership"}
                              onChange={() => selectAccessMode("membership")}
                            />
                            <span className="radio" aria-hidden="true" />
                            <span className="text">By membership plans</span>
                          </label>
                        </div>
                        <div className="scheduling-access-subsections">
                          {userTypeAvailable && (
                            <section
                              className={`scheduling-access-subsection${
                                accessModeEffective !== "user_type"
                                  ? " is-disabled"
                                  : ""
                              }`}
                              aria-disabled={
                                accessModeEffective !== "user_type"
                              }
                            >
                              <h3 className="scheduling-access-subsection-title">
                                User type
                              </h3>
                              <div className="scheduling-access-subsection-body">
                                {visibleUserTypes.map(({ id, label }) => (
                                  <label key={id} className="checkbox-input">
                                    <input
                                      type="checkbox"
                                      checked={userTypes[id]}
                                      disabled={
                                        accessModeEffective !== "user_type"
                                      }
                                      onChange={() => toggleUserType(id)}
                                    />
                                    <span
                                      className="checkbox"
                                      aria-hidden="true"
                                    />
                                    <span className="text">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </section>
                          )}
                          <section
                            className={`scheduling-access-subsection${
                              accessModeEffective !== "membership"
                                ? " is-disabled"
                                : ""
                            }`}
                            aria-disabled={accessModeEffective !== "membership"}
                          >
                            <div className="scheduling-access-subsection-header">
                              <h3 className="scheduling-access-subsection-title">
                                Memberships
                              </h3>
                              <button
                                type="button"
                                className="scheduling-settings-all"
                                disabled={accessModeEffective !== "membership"}
                                onClick={() =>
                                  setAllMemberships(!allMembershipsChecked)
                                }
                                aria-pressed={allMembershipsChecked}
                              >
                                <img
                                  src={
                                    allMembershipsChecked
                                      ? "/img/new-icons/checked-square.svg"
                                      : "/img/new-icons/unchecked-square.svg"
                                  }
                                  alt=""
                                  aria-hidden="true"
                                />
                                <span>All</span>
                              </button>
                            </div>
                            <div className="scheduling-access-subsection-body">
                              {plans.map(({ id, label }) => (
                                <label key={id} className="checkbox-input">
                                  <input
                                    type="checkbox"
                                    checked={memberships[id] ?? false}
                                    disabled={
                                      accessModeEffective !== "membership"
                                    }
                                    onChange={() => toggleMembership(id)}
                                  />
                                  <span
                                    className="checkbox"
                                    aria-hidden="true"
                                  />
                                  <span className="text" title={label}>
                                    {label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </section>
                        </div>
                      </>
                    )}
                    {showPullToggle && (
                      <div className="scheduling-access-row scheduling-access-row-switch scheduling-access-row-pull">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label="Pull users access"
                          aria-checked={pullEnabled}
                          checked={pullEnabled}
                          onChange={() => setPullEnabled(!pullEnabled)}
                        />
                        <span className="scheduling-access-row-text">
                          <span className="scheduling-access-row-title">
                            PULL USERS{" "}
                            <span className="scheduling-access-row-title-hint">
                              (1:1 training)
                            </span>
                          </span>
                          <span className="scheduling-access-row-desc">
                            Only PULL users can view and book this session.
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {submitError && (
            <p className="create-class-modal-error" role="alert">
              {submitError}
            </p>
          )}
          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleCreatePtSession}
              disabled={isSubmitting || !canSubmit}
            >
              {isSubmitting ? "Saving…" : "Save"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CreatePtSessionModal;
